import * as THREE from 'three';
import { CONTROLS } from './constants';

/**
 * Sets up mouse and keyboard controls for the scene
 */
const setupControls = (sceneData, earthGroup, pointCloudGroup = null) => {
  const { renderer, camera, scene } = sceneData;
  
  // Setup mouse controls for manual rotation
  let isDragging = false;
  let previousMousePosition = { x: 0, y: 0 };
  let activeObject = null; // Track which object is being rotated
  
  const mouseDownHandler = (e) => {
    isDragging = true;
    previousMousePosition = {
      x: e.clientX,
      y: e.clientY
    };
    
    // Determine which object to rotate based on where the user clicked
    // Calculate normalized device coordinates (-1 to +1) for raycasting
    const rect = renderer.domElement.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const mouseY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    
    // Set up raycaster
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera({ x: mouseX, y: mouseY }, camera);
    
    // Check for intersections with Earth and point cloud
    // Create a list of objects to check
    const objects = [];
    
    // Add the Earth object
    earthGroup.traverse(child => {
      if (child.isMesh) {
        objects.push(child);
      }
    });
    
    // Add the point cloud object if it exists
    if (pointCloudGroup) {
      pointCloudGroup.traverse(child => {
        if (child.isPoints || child.isMesh) {
          objects.push(child);
        }
      });
    }
    
    // Find intersections
    const intersects = raycaster.intersectObjects(objects, true);
    
    // Determine which object was clicked
    if (intersects.length > 0) {
      // Find which group the intersected object belongs to
      let currentObj = intersects[0].object;
      let foundInEarth = false;
      let foundInPointCloud = false;
      
      // Find if the object is part of earthGroup
      let parent = currentObj;
      while (parent) {
        if (parent === earthGroup) {
          foundInEarth = true;
          break;
        }
        parent = parent.parent;
      }
      
      // If not found in earth, check if it's in point cloud
      if (!foundInEarth && pointCloudGroup) {
        parent = currentObj;
        while (parent) {
          if (parent === pointCloudGroup) {
            foundInPointCloud = true;
            break;
          }
          parent = parent.parent;
        }
      }
      
      // Set active object based on intersection
      if (foundInEarth) {
        activeObject = earthGroup;
      } else if (foundInPointCloud) {
        activeObject = pointCloudGroup;
      } else {
        // If we can't determine, use a default based on position
        // This is a fallback - determine by position on screen
        if (mouseX < 0) { // Left side - Earth
          activeObject = earthGroup;
        } else { // Right side - Point cloud
          activeObject = pointCloudGroup;
        }
      }
    } else {
      // If no intersection, determine by position on screen
      if (mouseX < 0) { // Left side - Earth
        activeObject = earthGroup;
      } else { // Right side - Point cloud
        activeObject = pointCloudGroup;
      }
    }
  };
  
  const mouseMoveHandler = (e) => {
    if (!isDragging || !activeObject) return;
    
    // Temporarily disable auto rotation when user interacts
    const deltaMove = {
      x: e.clientX - previousMousePosition.x,
      y: e.clientY - previousMousePosition.y
    };
    
    // Rotate the active object
    if (activeObject === pointCloudGroup) {
      // Rotate the point cloud
      pointCloudGroup.rotation.z += deltaMove.x * CONTROLS.ROTATION_SPEED_MULTIPLIER;
      pointCloudGroup.rotation.x += deltaMove.y * CONTROLS.ROTATION_SPEED_MULTIPLIER;
    } else if (activeObject === earthGroup) {
      // Rotate the earth
      earthGroup.rotation.y += deltaMove.x * CONTROLS.ROTATION_SPEED_MULTIPLIER;
      earthGroup.rotation.x += deltaMove.y * CONTROLS.ROTATION_SPEED_MULTIPLIER;
    }
    
    previousMousePosition = {
      x: e.clientX,
      y: e.clientY
    };
  };
  
  const mouseUpHandler = () => {
    isDragging = false;
    activeObject = null; // Clear active object when mouse is released
  };
  
  const wheelHandler = (e) => {
    // Zoom in/out with mouse wheel
    e.preventDefault();
    camera.position.z += e.deltaY * CONTROLS.ZOOM.SPEED;
    // Keep a reasonable zoom limit
    camera.position.z = Math.max(CONTROLS.ZOOM.MIN, Math.min(camera.position.z, CONTROLS.ZOOM.MAX));
  };
  
  // Touch event handlers (similar to mouse events but for mobile)
  const touchStartHandler = (e) => {
    if (e.touches.length === 1) {
      e.preventDefault(); // Prevent scrolling on touch devices
      isDragging = true;
      
      const touch = e.touches[0];
      previousMousePosition = {
        x: touch.clientX,
        y: touch.clientY
      };
      
      // Determine which object to rotate based on where the user touched
      // Calculate normalized device coordinates (-1 to +1) for raycasting
      const rect = renderer.domElement.getBoundingClientRect();
      const touchX = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
      const touchY = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Set up raycaster
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera({ x: touchX, y: touchY }, camera);
      
      // Check for intersections with Earth and point cloud (same logic as mouse)
      const objects = [];
      
      // Add the Earth object
      earthGroup.traverse(child => {
        if (child.isMesh) {
          objects.push(child);
        }
      });
      
      // Add the point cloud object if it exists
      if (pointCloudGroup) {
        pointCloudGroup.traverse(child => {
          if (child.isPoints || child.isMesh) {
            objects.push(child);
          }
        });
      }
      
      // Find intersections
      const intersects = raycaster.intersectObjects(objects, true);
      
      // Determine which object was touched (same logic as mouse)
      if (intersects.length > 0) {
        let currentObj = intersects[0].object;
        let foundInEarth = false;
        let foundInPointCloud = false;
        
        // Find if the object is part of earthGroup
        let parent = currentObj;
        while (parent) {
          if (parent === earthGroup) {
            foundInEarth = true;
            break;
          }
          parent = parent.parent;
        }
        
        // If not found in earth, check if it's in point cloud
        if (!foundInEarth && pointCloudGroup) {
          parent = currentObj;
          while (parent) {
            if (parent === pointCloudGroup) {
              foundInPointCloud = true;
              break;
            }
            parent = parent.parent;
          }
        }
        
        // Set active object based on intersection
        if (foundInEarth) {
          activeObject = earthGroup;
        } else if (foundInPointCloud) {
          activeObject = pointCloudGroup;
        } else {
          // Fallback to screen position
          if (touchX < 0) { // Left side - Earth
            activeObject = earthGroup;
          } else { // Right side - Point cloud
            activeObject = pointCloudGroup;
          }
        }
      } else {
        // If no intersection, determine by position on screen
        if (touchX < 0) { // Left side - Earth
          activeObject = earthGroup;
        } else { // Right side - Point cloud
          activeObject = pointCloudGroup;
        }
      }
    }
  };
  
  const touchMoveHandler = (e) => {
    if (!isDragging || !activeObject || e.touches.length !== 1) return;
    
    e.preventDefault(); // Prevent scrolling
    const touch = e.touches[0];
    
    // Calculate movement delta
    const deltaMove = {
      x: touch.clientX - previousMousePosition.x,
      y: touch.clientY - previousMousePosition.y
    };
    
    // Rotate the active object (same logic as mouse)
    if (activeObject === pointCloudGroup) {
      pointCloudGroup.rotation.z += deltaMove.x * CONTROLS.ROTATION_SPEED_MULTIPLIER;
      pointCloudGroup.rotation.x += deltaMove.y * CONTROLS.ROTATION_SPEED_MULTIPLIER;
    } else if (activeObject === earthGroup) {
      earthGroup.rotation.y += deltaMove.x * CONTROLS.ROTATION_SPEED_MULTIPLIER;
      earthGroup.rotation.x += deltaMove.y * CONTROLS.ROTATION_SPEED_MULTIPLIER;
    }
    
    previousMousePosition = {
      x: touch.clientX,
      y: touch.clientY
    };
  };
  
  const touchEndHandler = () => {
    isDragging = false;
    activeObject = null;
  };
  
  // Pinch to zoom for mobile
  let initialPinchDistance = 0;
  
  const touchStartZoomHandler = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      
      // Calculate initial distance between finger points
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      initialPinchDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
    }
  };
  
  const touchMoveZoomHandler = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentPinchDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      if (initialPinchDistance > 0) {
        // Calculate zoom direction and speed
        const pinchDelta = currentPinchDistance - initialPinchDistance;
        camera.position.z -= pinchDelta * CONTROLS.ZOOM.SPEED * 0.5;
        
        // Keep a reasonable zoom limit
        camera.position.z = Math.max(CONTROLS.ZOOM.MIN, 
                                     Math.min(camera.position.z, CONTROLS.ZOOM.MAX));
        
        // Update initial distance for next move
        initialPinchDistance = currentPinchDistance;
      }
    }
  };
  
  // Add event listeners for both mouse and touch
  renderer.domElement.addEventListener('mousedown', mouseDownHandler);
  renderer.domElement.addEventListener('mousemove', mouseMoveHandler);
  renderer.domElement.addEventListener('mouseup', mouseUpHandler);
  renderer.domElement.addEventListener('mouseout', mouseUpHandler);
  renderer.domElement.addEventListener('wheel', wheelHandler);
  
  // Touch events
  renderer.domElement.addEventListener('touchstart', touchStartHandler, { passive: false });
  renderer.domElement.addEventListener('touchmove', touchMoveHandler, { passive: false });
  renderer.domElement.addEventListener('touchend', touchEndHandler);
  renderer.domElement.addEventListener('touchcancel', touchEndHandler);
  
  // Additional touch listeners for pinch zooming
  renderer.domElement.addEventListener('touchstart', touchStartZoomHandler, { passive: false });
  renderer.domElement.addEventListener('touchmove', touchMoveZoomHandler, { passive: false });
  
  // Return event handler references for cleanup
  return {
    mouseDownHandler,
    mouseMoveHandler,
    mouseUpHandler,
    wheelHandler,
    touchStartHandler,
    touchMoveHandler,
    touchEndHandler,
    touchStartZoomHandler,
    touchMoveZoomHandler,
    isDragging: () => isDragging
  };
};

/**
 * Removes event listeners
 */
const cleanupControls = (sceneData, controls) => {
  const { renderer } = sceneData;
  const { 
    mouseDownHandler, 
    mouseMoveHandler, 
    mouseUpHandler, 
    wheelHandler,
    touchStartHandler,
    touchMoveHandler,
    touchEndHandler,
    touchStartZoomHandler,
    touchMoveZoomHandler
  } = controls;
  
  // Remove mouse event listeners
  renderer.domElement.removeEventListener('mousedown', mouseDownHandler);
  renderer.domElement.removeEventListener('mousemove', mouseMoveHandler);
  renderer.domElement.removeEventListener('mouseup', mouseUpHandler);
  renderer.domElement.removeEventListener('mouseout', mouseUpHandler);
  renderer.domElement.removeEventListener('wheel', wheelHandler);
  
  // Remove touch event listeners
  renderer.domElement.removeEventListener('touchstart', touchStartHandler);
  renderer.domElement.removeEventListener('touchmove', touchMoveHandler);
  renderer.domElement.removeEventListener('touchend', touchEndHandler);
  renderer.domElement.removeEventListener('touchcancel', touchEndHandler);
  
  // Remove pinch zoom listeners
  renderer.domElement.removeEventListener('touchstart', touchStartZoomHandler);
  renderer.domElement.removeEventListener('touchmove', touchMoveZoomHandler);
};

export { setupControls, cleanupControls };